import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Card, Statistic, Skeleton } from "antd";
import { withTranslation } from "react-i18next";
import {
  resolveAlertMetrics,
  getWarningStatusText,
  getWarningColor as getWarningColorUtil,
  buildAlertsCardsModel,
} from "../../utils/shipmentAlertsUtils";

let green = "#3f8600";
let red = "#ca0909";

function Alerts({
  searchParameters,
  hasTelemetry,
  t,
  shipmentData,
  shipmentCode,
  telemetryInfo,
  isStandalone,
  variant = "full",
}) {
  const startValue = searchParameters?.startValue;
  const endValue = searchParameters?.endValue;

  const [alertParameters, setAlertParameters] = useState({
    message: "Cargando...",
    warningsImpact: 0,
    warningsOpen: 0,
    warningsTemp: 0,
    warningsHum: 0,
    warningsBatteryLvlContainer: 0,
    warningsBatteryLvlRemote: 0,
    warningsImpactColor: "",
    warningsOpenColor: "",
    warningsTempColor: "",
    warningsHumColor: "",
    warningsBatteryLvlContainerColor: "",
    warningsBatteryLvlRemoteColor: "",
    status: "loading",
    statusCode: 0,
    shipmentCode,
    startValue,
    endValue,
    mustShowPanel: false,
  });

  // LISTADO
  useEffect(() => {
    if (hasTelemetry) return;

    if (shipmentData?.statusCode === 0) {
      setAlertParameters((prev) => ({
        ...prev,
        message: "Cargando...",
        status: shipmentData.status,
        statusCode: 0,
        mustShowPanel: shipmentData.status === "paginating",
      }));
      return;
    }

    if (shipmentData?.statusCode !== 200) {
      setAlertParameters((prev) => ({
        ...prev,
        message: shipmentData?.message,
        status: "loaded",
        statusCode: shipmentData?.statusCode ?? 0,
        mustShowPanel: false,
      }));
      return;
    }

    if (!shipmentData?.data?.length) {
      setAlertParameters((prev) => ({
        ...prev,
        statusCode: 204,
        status: "loaded",
        message: "No se han encontrado datos.",
        mustShowPanel: false,
      }));
      return;
    }

    setAlertParameters((prev) => ({
      ...prev,
      status: "loaded",
      message: shipmentData.message,
      statusCode: shipmentData.statusCode,
      mustShowPanel: true,
    }));
  }, [shipmentData, hasTelemetry]);

  // MÉTRICAS por variante
  const metrics = useMemo(
    () =>
      resolveAlertMetrics({
        telemetryInfo,
        variant,
        isStandalone,
      }),
    [telemetryInfo, variant, isStandalone]
  );

  const { warnings } = metrics || {};
  const wImpact = warnings?.impact ?? 0;
  const wOpen = warnings?.open ?? 0;
  const wTemp = warnings?.temp ?? 0;
  const wHum = warnings?.hum ?? 0;

  // DETALLE
  useEffect(() => {
    if (!hasTelemetry) return;

    if (telemetryInfo?.statusCode !== 200) {
      setAlertParameters((prev) => ({
        ...prev,
        message: "No se han encontrado datos de alertas para este envío.",
        status: "loaded",
        statusCode: telemetryInfo?.statusCode ?? 0,
        mustShowPanel: false,
      }));
      return;
    }

    const colorArgs = { okColor: green, badColor: red };

    setAlertParameters((prev) => ({
      ...prev,
      status: "loaded",
      statusCode: telemetryInfo.statusCode,
      shipmentCode,
      warningsImpact: wImpact,
      warningsOpen: wOpen,
      warningsTemp: wTemp,
      warningsHum: wHum,
      warningsImpactColor: getWarningColorUtil(wImpact, colorArgs),
      warningsOpenColor: getWarningColorUtil(wOpen, colorArgs),
      warningsTempColor: getWarningColorUtil(wTemp, colorArgs),
      warningsHumColor: getWarningColorUtil(wHum, colorArgs),
      mustShowPanel: true,
    }));
  }, [hasTelemetry, telemetryInfo, shipmentCode, wImpact, wOpen, wTemp, wHum]);

  const cards = useMemo(() => {
    if (!alertParameters.mustShowPanel) return [];
    return buildAlertsCardsModel({
      isStandalone,
      alertParameters,
      getWarningStatusText,
    });
  }, [alertParameters, isStandalone]);

  return (
    <div className="Alertas">
      <h2>{t("pages_Home.alerts")}</h2>

      {alertParameters.status === "loading" && <Skeleton active />}

      {alertParameters.statusCode !== 200 &&
        alertParameters.status === "loaded" && <p>{alertParameters.message}</p>}

      {alertParameters.mustShowPanel && (
        <Row gutter={16}>
          {cards.map((c) => (
            <Col span={6} key={c.key}>
              <Card>
                <Statistic
                  title={c.title}
                  value={c.value}
                  precision={0}
                  valueStyle={{ color: c.color }}
                  suffix={c.suffix}
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
}

export default withTranslation()(Alerts);
